import {Group, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import {Title} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {readableTime} from 'util/DateUtil';

import {ClientCredentialSectionCredential} from './__generated/ClientCredentialSectionCredential';

export type ClientCredentialSectionSecrets = ExtractByTypename<
  ClientCredentialSectionCredential['oauthCredential'],
  'OAuthCredentialNode'
>['clientSecrets'][number];

export const CLIENT_CREDENTIAL_SECTION_CREDENTIAL_FRAGMENT: DocumentNode = gql`
  fragment ClientCredentialSectionCredential on BenchlingAppNode {
    id
    oauthCredential {
      ... on InaccessibleInfoNode {
        invalidId
      }
      ... on OAuthCredentialNode {
        clientId
        clientSecrets {
          suffix
          createdAt
        }
      }
    }
  }
`;

interface ClientSecretTableProps {
  clientSecrets: Array<{
    suffix: string;
    createdAt: string;
  }>;
  clientId: string;
  appName: string;
  title: string;
  onDelete: (id?: number) => Promise<void>;
  onRegenerate: (id?: number) => Promise<void>;
  dataTestAttrs?: DataTestAttrs;
}

export function ClientID({clientId}: {clientId: string}): JSX.Element {
  return (
    <Col md={6} className={classNames('u-width-full', css(styles.grayBox))}>
      <div>
        <span {...getTestProps({component: 'ClientSecretTable', element: 'clientId'})}>{clientId}</span>
        <div className="pull-right">
          <Tooltip container="body" placement={TooltipPlacement.RIGHT} title="Copy client ID key">
            <Button
              buttonStyle={ButtonStyle.CIRCLE_ICON}
              size={ButtonSize.MINI}
              className={css(styles.noRightPadding)}
              icon={<BenchlingIcon icon="copy" className="faded u-primary-pointer" />}
              onClick={() =>
                copyToClipboard(clientId, {
                  showToastOnSuccess: true,
                  toastText: 'Copied client ID to clipboard.',
                })
              }
            />
          </Tooltip>
        </div>
      </div>
    </Col>
  );
}

export function ClientSecretTable(props: ClientSecretTableProps): JSX.Element {
  async function generateSecret(): Promise<void> {
    await props.onRegenerate();
  }
  async function regenerateSecretWithConfirmation(): Promise<void> {
    await legacyRunModal(ConfirmRegenerateClientSecretModal, {
      handleRegenerateSecret: props.onRegenerate,
      appName: props.appName,
    });
  }
  async function deleteSecret(): Promise<void> {
    await props.onDelete();
  }

  const appHasCredentials = props.clientSecrets.length !== 0;

  return (
    <div className={classNames(css(styles.tableWrapper))} {...getTestProps(props.dataTestAttrs)}>
      <Row className={classNames(css(styles.clientCredentialsRow), css(styles.clientId))}>
        <Title>Client ID</Title>
        <ClientID clientId={props.clientId} />
      </Row>
      <Row className={css(styles.clientCredentialsRow)}>
        <Table
          numRows={props.clientSecrets.length}
          overflowVisible={true}
          fitContent={true}
          emptyTableText="No associated client secrets"
        >
          <Column
            header={
              <th className={classNames('simpleTable-header-cell')}>
                <Group spacing="0">
                  <Text>Client secret</Text>
                  {appHasCredentials ? null : (
                    <Button
                      buttonStyle={ButtonStyle.CIRCLE_ICON}
                      size={ButtonSize.SMALL}
                      className="simpleDivTable-addButton"
                      icon="create"
                      dataTestAttrs={{component: 'ClientSecretTable', element: 'generateSecretButton'}}
                      onClick={generateSecret}
                    />
                  )}
                </Group>
              </th>
            }
            renderCell={(rowIndex: number): JSX.Element => (
              <Cell>
                <div className={css(styles.secretContainer)}>
                  <span>**********{props.clientSecrets[rowIndex].suffix}</span>
                </div>
              </Cell>
            )}
          />
          <Column
            header="Date created"
            renderCell={(rowIndex: number): JSX.Element => (
              <Cell>
                <div>
                  <span>{readableTime(props.clientSecrets[rowIndex].createdAt)}</span>
                </div>
              </Cell>
            )}
          />
          <Column
            header={<Cell className={css(styles.tooltip)} />}
            renderCell={(_: number): JSX.Element => (
              <Cell className={css(styles.tooltip)}>
                <Tooltip container="body" placement={TooltipPlacement.LEFT} title="Delete client secret">
                  <Button
                    buttonStyle={ButtonStyle.CIRCLE_ICON}
                    size={ButtonSize.SMALL}
                    icon={<BenchlingIcon icon="delete" className="faded u-danger-pointer" />}
                    onClick={() => {
                      legacyRunModal(ConfirmDeleteClientSecretModal, {
                        appName: props.appName,
                        handleDelete: deleteSecret,
                      });
                    }}
                  />
                </Tooltip>
                <Tooltip container="body" placement={TooltipPlacement.RIGHT} title="Regenerate client secret">
                  <Button
                    buttonStyle={ButtonStyle.CIRCLE_ICON}
                    size={ButtonSize.SMALL}
                    onClick={regenerateSecretWithConfirmation}
                    icon={<BenchlingIcon icon="arrows-rotate" className="faded u-primary-pointer" />}
                    dataTestAttrs={{component: 'ClientSecretTable', element: 'generateSecretButton'}}
                  />
                </Tooltip>
              </Cell>
            )}
          />
        </Table>
      </Row>
    </div>
  );
}

export function ConfirmDeleteClientSecretModal({
  handleDelete,
  appName,
  onRequestClose,
  onRequestSubmit,
}: {
  handleDelete: (id?: number) => void;
  appName: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}): JSX.Element {
  function handleSubmit(): void {
    handleDelete();
    onRequestSubmit();
  }
  return (
    <Modal
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      title="Confirm Client Secret Deletion"
      buttons={[{text: 'Delete Client Secret', submit: true, style: ButtonStyle.NEGATIVE}]}
    >
      Confirm you’d like to remove <b>{appName}</b>'s Client Secret. Access for any future requests using this
      Client Secret will be revoked.
    </Modal>
  );
}

export function ConfirmRegenerateClientSecretModal({
  handleRegenerateSecret,
  appName,
  onRequestClose,
  onRequestSubmit,
}: {
  handleRegenerateSecret: (id?: number) => Promise<void>;
  appName: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}): JSX.Element {
  async function handleSubmit(): Promise<void> {
    await handleRegenerateSecret();
    onRequestSubmit();
  }
  return (
    <Modal
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      title="Confirm Client Secret Regeneration"
      buttons={[{text: 'Regenerate Client Secret', submit: true, style: ButtonStyle.SECONDARY}]}
    >
      Generating a new Client Secret will delete <b>{appName}</b>'s current one.
    </Modal>
  );
}

export default function ClientCredentialSection({
  clientSecrets,
  appName,
  clientId,
  handleRegenerateSecret,
  handleDelete,
}: {
  clientSecrets: Array<ClientCredentialSectionSecrets>;
  clientId: string;
  appName: string;
  handleRegenerateSecret: (id?: number) => Promise<void>;
  handleDelete: (id?: number) => Promise<void>;
}): JSX.Element {
  return (
    <ClientSecretTable
      appName={appName}
      title="Client Secrets"
      clientSecrets={clientSecrets}
      onDelete={handleDelete}
      onRegenerate={handleRegenerateSecret}
      clientId={clientId}
      dataTestAttrs={{component: 'ClientCredentialSection', element: 'client'}}
    />
  );
}

const styles = StyleSheet.create({
  secretContainer: {
    display: 'inline-block',
    width: 'calc(100% - 50px)',
  },
  secret: {
    fontSize: 20,
  },
  pullLeft: {
    paddingLeft: 0,
  },
  tableWrapper: {
    marginLeft: 0,
    display: 'inline-block',
    width: '60%',
    minWidth: 390,
  },
  noRightPadding: {
    paddingRight: 0,
  },
  grayBox: {
    backgroundColor: GRAY_LIGHTER,
    padding: 10,
  },
  tooltip: {
    textAlign: 'right',
    width: 20,
  },
  clientCredentialsRow: {
    marginLeft: '0px',
    marginTop: '10px',
  },
  clientId: {
    width: '270px',
  },
});
